/**
 * Custom Subscription Widget Controller for Seal Subscriptions
 * With Dynamic Feature Text Updates
 */

class SubscriptionWidgetController {
    constructor() {
        this.selectedPlan = null;
        this.sealContainer = '.sealsubs-target-element'; // Seal widget container
        this.addToCartForm = 'form[action="/cart/add"]'; // Your add to cart form
        this.init();
    }

    init() {
        this.hideSealWidget();
        this.attachEventListeners();
        this.selectDefaultPlan();
    }

    /**
     * Hide the Seal Subscriptions widget with CSS
     */
    hideSealWidget() {
        const sealWidget = document.querySelector(this.sealContainer);
        if (sealWidget) {
            sealWidget.style.display = 'none';
            console.log('✅ Seal widget hidden');
        }
    }

    /**
     * Attach event listeners to radio buttons and action buttons
     */
    attachEventListeners() {
        // Radio button selection
        const radioButtons = document.querySelectorAll('.radio-input');
        radioButtons.forEach(radio => {
            radio.addEventListener('change', (e) => this.handlePlanSelection(e));
        });

        // Click on entire option box
        const options = document.querySelectorAll('.subscription-option');
        options.forEach(option => {
            option.addEventListener('click', (e) => {
                if (e.target.classList.contains('radio-input')) return;
                const radio = option.querySelector('.radio-input');
                if (radio) {
                    radio.checked = true;
                    this.handlePlanSelection({ target: radio });
                }
            });
        });

        // Start subscription button
        document.getElementById('startSubscription')?.addEventListener('click', () => {
            this.handleSubscriptionStart();
        });

        // One-time purchase button
        document.getElementById('buyOnetime')?.addEventListener('click', () => {
            this.handleOnetimePurchase();
        });
    }

    /**
     * Select default plan (first subscription option)
     */
    selectDefaultPlan() {
        const firstRadio = document.querySelector('.radio-input');
        if (firstRadio) {
            firstRadio.checked = true;
            this.handlePlanSelection({ target: firstRadio });
        }
    }

    /**
     * Handle plan selection
     */
    handlePlanSelection(event) {
        const radio = event.target;
        const option = radio.closest('.subscription-option');
        
        // Remove selected class from all options
        document.querySelectorAll('.subscription-option').forEach(opt => {
            opt.classList.remove('selected');
        });

        // Add selected class to current option
        option.classList.add('selected');

        // Store selected plan data
        this.selectedPlan = {
            planType: option.dataset.plan,
            sellingPlanId: option.dataset.sellingPlanId,
            packs: option.dataset.packs,
            weeks: option.dataset.weeks,
            gummies: option.dataset.gummies,
            shipping: option.dataset.shipping,
            element: option
        };

        console.log('📦 Selected Plan:', this.selectedPlan);

        // Update dynamic feature text
        this.updateDynamicText();

        // Auto-select in hidden Seal widget
        this.syncWithSealWidget();
    }

    /**
     * Update dynamic feature text based on selected plan
     */
    updateDynamicText() {
        if (!this.selectedPlan) return;

        // Update packs/weeks/gummies text
        const packsText = document.getElementById('dynamic-packs-text');
        if (packsText && this.selectedPlan.packs && this.selectedPlan.weeks && this.selectedPlan.gummies) {
            packsText.textContent = `${this.selectedPlan.packs} servings every ${this.selectedPlan.weeks} week (${this.selectedPlan.gummies} gummies)`;
            console.log('✅ Updated packs text:', packsText.textContent);
        }

        // Update shipping text
        const shippingText = document.getElementById('dynamic-shipping-text');
        if (shippingText && this.selectedPlan.shipping) {
            shippingText.textContent = this.selectedPlan.shipping;
            console.log('✅ Updated shipping text:', shippingText.textContent);
        }
    }

    /**
     * Sync selection with Seal Subscriptions widget
     * Updates the hidden input and selects the correct plan
     */
    syncWithSealWidget() {
        if (!this.selectedPlan) return;

        // Find the Seal subscription radio button (not one-time)
        const sealSubscriptionRadio = document.querySelector('input[data-type="subscription"]');
        
        if (sealSubscriptionRadio) {
            // Select subscription option (not one-time)
            sealSubscriptionRadio.checked = true;
            
            // Trigger change event
            sealSubscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
            
            console.log('✅ Seal subscription radio selected');
        }

        // Find the Seal selling plan dropdown
        const sealSelect = document.querySelector('select[name="subs_interval"]');
        
        if (sealSelect && this.selectedPlan.sellingPlanId) {
            // Select the correct selling plan option
            const option = sealSelect.querySelector(`option[value="${this.selectedPlan.sellingPlanId}"]`);
            
            if (option) {
                sealSelect.value = this.selectedPlan.sellingPlanId;
                
                // Trigger change event
                sealSelect.dispatchEvent(new Event('change', { bubbles: true }));
                
                console.log('✅ Seal selling plan selected:', this.selectedPlan.sellingPlanId);
            }
        }

        // Update the hidden selling_plan input in the form
        this.updateFormSellingPlan();
    }

    /**
     * Update the hidden selling_plan input in the add to cart form
     */
    updateFormSellingPlan() {
        // Update Seal's hidden input
        const sealHiddenInput = document.querySelector('input[name="selling_plan"][data-sls-selling_plan]');
        if (sealHiddenInput && this.selectedPlan.sellingPlanId) {
            sealHiddenInput.value = this.selectedPlan.sellingPlanId;
            console.log('✅ Seal hidden input updated:', this.selectedPlan.sellingPlanId);
        }

        // Update form's hidden input
        const formHiddenInput = document.querySelector('form[action="/cart/add"] input[name="selling_plan"]');
        if (formHiddenInput && this.selectedPlan.sellingPlanId) {
            formHiddenInput.value = this.selectedPlan.sellingPlanId;
            console.log('✅ Form hidden input updated:', this.selectedPlan.sellingPlanId);
        }
    }

    /**
     * Handle subscription start - trigger the actual add to cart button
     */
    handleSubscriptionStart() {
        if (!this.selectedPlan) {
            alert('Please select a subscription plan');
            return;
        }

        console.log('🚀 Starting subscription with plan:', this.selectedPlan.sellingPlanId);

        // Make sure the form has the correct selling plan
        this.updateFormSellingPlan();

        // Find and click the actual "Add to cart" button
        const addToCartButton = document.querySelector('button[type="submit"][name="add"]');
        
        if (addToCartButton) {
            // Click the button
            addToCartButton.click();
            console.log('✅ Add to cart button clicked');
        } else {
            console.error('❌ Add to cart button not found');
            alert('Error: Could not find add to cart button');
        }
    }

    /**
     * Handle one-time purchase
     */
    handleOnetimePurchase() {
        console.log('🛒 Processing one-time purchase');

        // Select one-time purchase in Seal widget
        const oneTimeRadio = document.querySelector('input[data-type="one_time"]');
        
        if (oneTimeRadio) {
            oneTimeRadio.checked = true;
            oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
            console.log('✅ One-time purchase selected in Seal widget');
        }

        // Clear selling_plan from form (one-time purchase shouldn't have selling plan)
        const formHiddenInput = document.querySelector('form[action="/cart/add"] input[name="selling_plan"]');
        if (formHiddenInput) {
            formHiddenInput.value = '';
            console.log('✅ Selling plan cleared for one-time purchase');
        }

        const sealHiddenInput = document.querySelector('input[name="selling_plan"][data-sls-selling_plan]');
        if (sealHiddenInput) {
            sealHiddenInput.value = '';
        }

        // Click the add to cart button
        const addToCartButton = document.querySelector('button[type="submit"][name="add"]');
        
        if (addToCartButton) {
            addToCartButton.click();
            console.log('✅ Add to cart button clicked for one-time purchase');
        } else {
            console.error('❌ Add to cart button not found');
            alert('Error: Could not find add to cart button');
        }
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new SubscriptionWidgetController();
    });
} else {
    new SubscriptionWidgetController();
}

// Export for use in other scripts
window.SubscriptionWidgetController = SubscriptionWidgetController;